import { InfoHint } from '@eo/shared';

import { TERM_HELP } from './constants';
import { formatHours } from './helpers';
import type { PortfolioSummaryProps } from './types';

export function PortfolioSummary({ report }: PortfolioSummaryProps) {
  const { totals, progress } = report;
  const engagement = totals.budget.fixed + totals.budget.variable;
  const { cfo, dataTeam } = totals.byRole;
  const delegated = dataTeam.assignedFixed + dataTeam.assignedVariable;
  const loggedBy = (side: typeof cfo) => formatHours(side.actualFixed + side.actualVariable);

  const figures: Array<{ label: string; value: string; caption?: string; help?: string }> = [
    { label: 'Clients', value: String(totals.clientCount) },
    { label: 'Engagement', value: formatHours(engagement), help: TERM_HELP.engagement },
    {
      label: 'Leverage',
      value: `${engagement > 0 ? Math.round((delegated / engagement) * 100) : 0}%`,
      caption: `${formatHours(delegated)} on the Data team`,
      help: TERM_HELP.leverage,
    },
    {
      label: 'Logged',
      value: formatHours(totals.actual.fixed + totals.actual.variable),
      caption: `CFO ${loggedBy(cfo)} · Data team ${loggedBy(dataTeam)}`,
      help: TERM_HELP.logged,
    },
    {
      label: 'Month',
      value: `${progress.elapsedWorkingDays} of ${progress.workingDays}`,
      caption: 'working days',
      help: TERM_HELP.month,
    },
  ];

  return (
    <dl
      data-testid="portfolio-summary"
      className="grid grid-cols-2 gap-6 sm:grid-cols-3 xl:grid-cols-5"
    >
      {figures.map((f) => (
        <div key={f.label} className="flex flex-col gap-1 border-l border-border-faint pl-3">
          <dt className="flex items-center gap-1 text-xs text-text-tertiary">
            {f.label}
            {f.help && <InfoHint term={f.label}>{f.help}</InfoHint>}
          </dt>
          <dd className="text-2xl leading-[26px] tabular-nums text-text-secondary">{f.value}</dd>
          {f.caption && <dd className="text-xs text-text-tertiary">{f.caption}</dd>}
        </div>
      ))}
    </dl>
  );
}
